import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Dialog } from "antd-mobile";
import { QRCodeCanvas } from "qrcode.react";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../../components/Toast";
import { copyTextToClipboard } from "../../utils/copy";

const usdtImg = require("../../assets/images/wallet/usdt.png");

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #f7f8ff;
  padding: 12px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  text-align: center;
`;

const Warn = styled.div`
  color: rgb(223, 33, 33);
  font-size: 14px;
  margin: 8px 0;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  text-align: left;
`;

const ExchangeDetail = () => {
  const { params, navigate } = useNavigation();
  const { apiStore, authStore, walletStore } = useStores();
  const [mins, setMinutes] = useState(14);
  const [secs, setSeconds] = useState(59);
  const [endTimestamp, setEndTimestamp] = useState(0);
  const [loading, setLoading] = useState(false);

  const detail = useMemo(() => {
    const fromList =
      walletStore.exchangeList?.find?.(
        (x: any) => x.ex_order_no === params.ex_order_no
      ) || walletStore.getExchange?.();
    return {
      ...fromList,
      usdt_rand: params.usdt_rand || fromList?.usdt_rand || fromList?.ex_amount,
      usdt_trc: params.usdt_trc || fromList?.usdt_trc,
      amount: params.amount || fromList?.amount,
      ex_order_no: params.ex_order_no || fromList?.ex_order_no,
      created: params.created || fromList?.created,
    };
  }, [params, walletStore]);

  useEffect(() => {
    walletStore.getExchangeList?.();
    const created = detail.created ? new Date(detail.created).getTime() : Date.now();
    setEndTimestamp(created + 1000 * 60 * 15);
  }, [detail.created, walletStore]);

  useEffect(() => {
    if (!endTimestamp) return undefined;
    const timer = setInterval(() => {
      const diff = Math.floor((endTimestamp - Date.now()) / 1000);
      setMinutes(Math.max(0, Math.floor(diff / 60)));
      setSeconds(Math.max(0, diff % 60));
    }, 1000);
    return () => clearInterval(timer);
  }, [endTimestamp]);

  const isOutTime = mins <= 0 && secs <= 0;

  const cancel = () => {
    Dialog.confirm({
      content: "确定取消充值订单？",
      onConfirm: async () => {
        try {
          setLoading(true);
          await apiStore.cancelExchange({
            token: authStore.getToken,
            ex_order_no: detail.ex_order_no,
          });
          await walletStore.getExchangeList?.();
          ToastSuccess("取消成功");
          navigate("Wallet");
        } catch (e: any) {
          ToastError(e.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <PageWrap>
      <PageHeader title="充值详情" />
      <ScrollArea>
        <Card style={{ position: "relative" }}>
          {isOutTime && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.8)",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}
            >
              订单已超时
            </div>
          )}
          <img src={usdtImg} alt="" width={36} height={36} />
          <Warn>请务必转账下方精确金额（含小数）</Warn>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#1c9cea" }}>
            {detail.usdt_rand} USDT
          </div>
          <div style={{ margin: "12px 0" }}>
            {detail.usdt_trc ? (
              <QRCodeCanvas value={String(detail.usdt_trc)} size={160} />
            ) : null}
          </div>
          <Row>
            <span>收款地址</span>
            <span
              style={{ color: "#1c9cea", cursor: "pointer", maxWidth: "60%", wordBreak: "break-all" }}
              onClick={() => {
                copyTextToClipboard(String(detail.usdt_trc || ""));
                ToastSuccess("已复制地址");
              }}
            >
              {detail.usdt_trc || "-"} 复制
            </span>
          </Row>
          <Row>
            <span>到账喜鹊币</span>
            <span>{detail.amount}</span>
          </Row>
          <Row>
            <span>订单号</span>
            <span>{detail.ex_order_no}</span>
          </Row>
          <Row>
            <span>剩余时间</span>
            <span style={{ color: "rgb(223,33,33)" }}>
              {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
            </span>
          </Row>
        </Card>
        <Button color="danger" block loading={loading} disabled={isOutTime} onClick={cancel}>
          取消订单
        </Button>
        <Button
          color="primary"
          block
          style={{ marginTop: 10 }}
          disabled={isOutTime}
          onClick={() =>
            navigate("ExchangeSuccess", { ex_order_no: detail.ex_order_no })
          }
        >
          我已转账
        </Button>
      </ScrollArea>
    </PageWrap>
  );
};

export default observer(ExchangeDetail);
